import { useEffect, useMemo, useRef, useState } from 'react'
import {
  ForkFilterOption,
  FrontendQuestionKind,
  NewFilterOption,
  SelectedFilter
} from '../../data/model/results'
import useDebounce from '../../hooks/useDebounce'

/** How many filters a page of the panel asks for. */
export const FILTERS_PAGE_SIZE = 20

/**
 * Identifies a panel's request criteria by value rather than by array identity,
 * so a re-render with an equivalent selection list is not mistaken for a change.
 */
export const getSelectionsKey = (selectedFilters: SelectedFilter[]): string =>
  selectedFilters
    .map((filter) =>
      [
        filter.questionId ?? filter.forkId ?? '',
        filter.marketLabel ?? '',
        (filter.responseOptionIds ?? []).join(','),
        (filter.branchNumbers ?? []).join(',')
      ].join(':')
    )
    .join('|')

/**
 * Search + pagination state for a filter panel, shared by the per-survey and
 * combined containers.
 *
 * The offset resets whenever the result set itself changes — a new search,
 * language or selection. The data hook appends while offset > 0, so re-fetching
 * a later page under new criteria would otherwise leave the panel showing that
 * page's slice alone, with a "load more" that has lost its place.
 */
export const useFilterPanelState = (
  language: string | undefined,
  selectedFilters: SelectedFilter[]
) => {
  const [currentOffset, setCurrentOffset] = useState<number>(0)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const debouncedSearchQuery = useDebounce(searchQuery, 300)
  const selectionsKey = useMemo(
    () => getSelectionsKey(selectedFilters),
    [selectedFilters]
  )

  useEffect(() => {
    setCurrentOffset(0)
  }, [debouncedSearchQuery, language, selectionsKey])

  return {
    currentOffset,
    setCurrentOffset,
    searchQuery,
    setSearchQuery,
    debouncedSearchQuery
  }
}

// A questionId is only unique within a single market: the combined view
// legitimately lists one twice when a market-split cluster (e.g. Region NL vs
// Region BE) has disjoint option sets, so entries are keyed by both. Collapses
// to the questionId alone in the per-survey view, where marketLabel is never set.
export const getFilterKey = (
  questionId: string | undefined,
  marketLabel: string | null | undefined
): string => `${questionId}::${marketLabel ?? ''}`

/**
 * Selects every audience question and fork filter on first load, so the panel
 * opens showing the criteria a researcher usually breaks results down by. The
 * entries carry no response options, so they widen nothing until options are
 * ticked.
 */
export const useAutoSelectDefaultFilters = (
  filters: NewFilterOption[],
  forkFilters: ForkFilterOption[],
  selectedFilters: SelectedFilter[],
  setSelectedFilters: (selectedFilters: SelectedFilter[]) => void
) => {
  const autoInitialized = useRef<boolean>(false)

  useEffect(() => {
    if (
      autoInitialized.current ||
      (filters.length === 0 && forkFilters.length === 0)
    ) {
      return
    }

    const existingKeys = new Set(
      selectedFilters.map((filter) =>
        getFilterKey(filter.questionId, filter.marketLabel)
      )
    )
    const existingForkIds = new Set(
      selectedFilters.map((filter) => filter.forkId)
    )

    // Includes the combined view's synthetic Market question, which the backend
    // returns as an ordinary audience filter.
    const audienceFiltersToAdd = filters
      .filter(
        (filter) =>
          filter.questionKind === FrontendQuestionKind.Audience &&
          !existingKeys.has(getFilterKey(filter.questionId, filter.marketLabel))
      )
      .map((question) => ({
        questionId: question.questionId,
        marketLabel: question.marketLabel,
        responseOptionIds: []
      }))

    const forkFiltersToAdd = forkFilters
      .filter((fork) => !existingForkIds.has(fork.id))
      .map((fork) => ({
        forkId: fork.id,
        branchNumbers: []
      }))

    const newFiltersToAdd = [...audienceFiltersToAdd, ...forkFiltersToAdd]

    if (newFiltersToAdd.length > 0) {
      setSelectedFilters([...selectedFilters, ...newFiltersToAdd])
    }

    autoInitialized.current = true
  }, [filters, forkFilters, selectedFilters, setSelectedFilters])
}
